import { useEffect, useState, type ElementType } from "react";
import { Button } from "../Button";
import { FIGURE_META } from "./figure-meta";

/**
 * 触れる図解 (`@noiman/figures`) をレッスン本文に埋め込む薄いラッパー。
 *
 * 図解本体は React に依存しない Custom Element (`<cd-figure>`) で、
 * `/figures/runtime.js` として本文バンドルの外に置いてある。ここは
 * 「ランタイムを1度だけ読み込んでタグを置く」だけの責務にとどめる。
 */

/**
 * 表示上限幅。figures の runtime.ts の figureMaxWidth と同じ式。
 * ここだけ違うと、静止画から本体に入れ替わった瞬間に幅が飛ぶ。
 * 資料を写した横長の図 (設計座標 1600 超) だけ 1040 まで許す
 */
function figureMaxWidth(width: number): number {
  return Math.min(1040, Math.max(760, width * 0.7), width * 2.2);
}

const RUNTIME_ID = "cd-figure-runtime";
const RUNTIME_SRC = "/figures/runtime.js";

/** React の JSX 型に無いタグなので ElementType 経由で描画する */
const CdFigure = "cd-figure" as unknown as ElementType;

export interface InteractiveFigureProps {
  /** 図解 id。`/figures/{src}.js` を読む。例 "array-index" */
  src: string;
  /** "off" で予測ファーストの3択ゲートを出さない */
  predict?: "on" | "off";
  /** 読み込み失敗時に出す静的画像の URL */
  fallback?: string;
  /**
   * "bare" で図解自身の見出しと説明文を出さない。
   * スライドデッキのように、外側が見出しを持つ場所で使う。
   */
  chrome?: "full" | "bare";
  /** 固定サイズのスライド内で操作UIと描画面を同時に収める */
  density?: "default" | "compact";
  /**
   * "on" で読者の設定に関わらず先頭パラメータを1往復させる。
   * "loop" は止めるまで繰り返す。一覧のサムネのように「これは動く」と
   * 伝えること自体が目的の場所で使う。
   * 省略時はランタイム側の既定 (読者の設定) に従う
   */
  autoplay?: "on" | "off" | "loop";
  /** 本文先頭の LCP 候補なら、静止画と図解ランタイムを先読みする */
  priority?: boolean;
  className?: string;
}

let runtimePromise: Promise<void> | null = null;

function loadRuntime(): Promise<void> {
  if (runtimePromise) return runtimePromise;
  runtimePromise = new Promise<void>((resolve, reject) => {
    const existing = document.getElementById(RUNTIME_ID);
    if (existing) {
      resolve();
      return;
    }
    const script = document.createElement("script");
    script.id = RUNTIME_ID;
    script.type = "module";
    script.src = RUNTIME_SRC;
    script.addEventListener("load", () => resolve());
    script.addEventListener("error", () =>
      reject(new Error("figure runtime の読み込みに失敗しました")),
    );
    document.head.appendChild(script);
  });
  return runtimePromise;
}

export function InteractiveFigure({
  src,
  predict = "on",
  fallback,
  chrome = "full",
  density = "default",
  autoplay,
  priority = false,
  className,
}: InteractiveFigureProps) {
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    // SSR では何もしない。ハイドレート後にランタイムを読む
    loadRuntime().catch(() => setFailed(true));
  }, []);

  /** 失敗した script タグを捨てて読み直す。ページ全体の再読み込みは不要 */
  const retry = () => {
    runtimePromise = null;
    document.getElementById(RUNTIME_ID)?.remove();
    setFailed(false);
    loadRuntime().catch(() => setFailed(true));
  };

  if (failed) {
    return (
      <div className={className}>
        {fallback ? (
          <img src={fallback} alt="図解" className="max-w-full rounded-lg" />
        ) : null}
        <p className="max-sm:text-[0.9375rem] text-sm text-sub">図解を読み込めませんでした。</p>
        <Button variant="outline" size="sm" onClick={retry}>
          もう一度読み込む
        </Button>
      </div>
    );
  }

  const meta = FIGURE_META[src];
  const still = fallback ?? (meta ? `/figures/still/${src}.svg` : undefined);

  // http-journey は操作が2段あり、静止画だけの SSR 高さから操作UIつきの
  // Custom Elementへ切り替わるとモバイルで約271px伸びる。先頭図解では
  // 読み込み中から同じ領域を確保し、後続本文を押し下げない。
  const reserveUi = priority && src === "http-journey";

  return (
    <>
      {priority ? (
        <>
          <link rel="modulepreload" href={RUNTIME_SRC} />
          <link rel="modulepreload" href={`/figures/${src}.js`} />
        </>
      ) : null}
      <CdFigure
        src={src}
        predict={predict}
        chrome={chrome}
        density={density}
        autoplay={autoplay}
        fallback={still}
        class={className}
        style={{ display: "block" }}
      >
        {/*
          静止画。Custom Element が shadow root を張った時点で消える。
          つまり JS が動く人には一瞬のプレースホルダー、動かない人と
          クローラーにはこれが図解そのものになる。SSR に出ることが肝心で、
          これが無いと本文の先頭が空白のまま index されていた
        */}
        {still ? (
          <img
            src={still}
            // caption は「スライダーを動かして…」という操作の指示なので alt に混ぜない。
            // 動かせない状態の人に、できない操作を読み上げることになる
            alt={meta?.title ?? "図解"}
            width={meta?.w}
            height={meta?.h}
            fetchPriority={priority ? "high" : "auto"}
            // 上限はランタイム側と同じ式で出す。ここだけ違うと、
            // 静止画から本体に入れ替わった瞬間に幅が飛ぶ
            style={{
              // compact は Custom Element の読み込み前から同じ高さに収める。
              // runtime 側だけを小さくすると、cold load 中の静止画が
              // スライド下部のページ送りへ重なり、読み込み後に急に縮む。
              width: density === "compact" ? "auto" : "100%",
              maxWidth: figureMaxWidth(meta?.w ?? 320),
              maxHeight: density === "compact" ? 190 : undefined,
              height: "auto",
              marginInline: density === "compact" ? "auto" : undefined,
            }}
          />
        ) : null}
        {reserveUi ? (
          <span
            aria-hidden="true"
            className="block h-[271px] rounded-b-xl bg-second md:h-[293px]"
          />
        ) : null}
      </CdFigure>
    </>
  );
}

export default InteractiveFigure;
